import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PaymentProviderNameEnum } from '@generated/graphql';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, of, skip, switchMap, take, tap } from 'rxjs';
import { Country } from '@modules/country/models/country.model';
import { WireTransferBankService } from '@modules/wire-transfer-bank/services/wire-transfer-bank.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { WireTransferBank } from '@modules/wire-transfer-bank/models/wire-transfer-bank.model';
import { FormBuilder, FormControl, FormGroup, Validators, } from '@angular/forms';
import { WireTransferFormType } from '../../models/forms/wire-transfer-form-type';
import {
  DropzoneAction,
  DropzoneFileChangedEvent,
} from '@shared/component/common/dropzone-upload/dropzone-upload.component';
import { PaymentService } from '../../services/payment.service';
import {
  BusinessProfileService
} from '@modules/account-settings/pages/business-profile/services/business-profile.service';
import { BillingAddress } from '@modules/address/models/billing-address.model';
import { MatRadioChange } from "@angular/material/radio";
import { Order } from "@modules/order/models/order.model";
import { startWith } from "rxjs/operators";
import { CheckoutFlowFacadeService } from '@shared/component/payments/services/checkout-flow-facade.service';

@UntilDestroy()
@Component({
  selector: 'app-payment-methods',
  templateUrl: './payment-methods.component.html',
  styleUrls: ['./payment-methods.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentMethodsComponent implements OnInit, OnDestroy {
  @Input() set country(value: Country) {
    this._country = value;
    this.banks$ = value ? this.wireTransferBankService.getByCountryId$(value.id) : of([]);
  }
  @Input() set order(value: Order) {
    this._order = value;
    this.orderSubject.next(value ?? null);
    this.syncOrderToForm();
  }
  @Input() redirectUrl: any[];
  @Input() redirectQueryParams?: Record<string, string>;
  @Input() shouldRedirect = true;

  readonly PaymentProviderNameEnum = PaymentProviderNameEnum;

  banks$: Observable<WireTransferBank[]> = of([]);

  wireTransferForm: FormGroup<WireTransferFormType>;

  paymentProviderOptions$ = this.checkoutFlowFacade.paymentProviderOptions$;
  readonly selectedPaymentProvider$ = this.checkoutFlowFacade.selectedPaymentProvider$;
  readonly wireTransferSubmitting$ = this.checkoutFlowFacade.wireTransferSubmitting$;

  private readonly orderSubject = new BehaviorSubject<Order | null>(null);
  private _order: Order;
  private _country: Country | null = null;

  businessProfile$ = this.businessProfileService.activeEntity$;
  isPaymentLocked$: Observable<boolean>;
  disabledSubmitWireTransferPayment$: Observable<boolean>;

  constructor(
    private readonly wireTransferBankService: WireTransferBankService,
    private readonly fb: FormBuilder,
    private readonly paymentService: PaymentService,
    private readonly businessProfileService: BusinessProfileService,
    private readonly checkoutFlowFacade: CheckoutFlowFacadeService,
  ) {
    this.disabledSubmitWireTransferPayment$ = of(true);
    this.isPaymentLocked$ = of(true);
  }

  get order(): Order {
    return this._order;
  }

  get country(): Country | null {
    return this._country;
  }

  ngOnInit(): void {
    this.createWireTransferForm();

    const providerSelection$ = this.selectedPaymentProvider$.pipe(startWith(this.checkoutFlowFacade.selectedPaymentProvider));
    const formValid$ = this.wireTransferForm.statusChanges.pipe(
      startWith(this.wireTransferForm.status),
      map(status => status === 'VALID'),
    );

    this.isPaymentLocked$ = combineLatest([
      this.orderSubject,
      this.paymentService.processingPaymentIds$,
    ]).pipe(
      map(([order, processingIds]) => {
        const isProcessing = !!order?.payment?.id && processingIds.has(order.payment.id);
        const canMakePayment = !!order?.payment?.canMakePayment;
        return isProcessing || !canMakePayment;
      }),
      distinctUntilChanged(),
    );

    this.disabledSubmitWireTransferPayment$ = combineLatest([
      this.checkoutFlowFacade.selectedBillingAddress$,
      this.orderSubject,
      this.paymentService.processingPaymentIds$,
      this.wireTransferSubmitting$,
      providerSelection$,
      formValid$,
    ]).pipe(
      map(([billingAddress, order, processingIds, submitting, providerValue, formValid]) => {
        const isProcessing = !!order?.payment?.id && processingIds.has(order.payment.id);
        return (
          !(billingAddress instanceof BillingAddress) ||
          !formValid ||
          submitting ||
          !providerValue ||
          !order?.payment?.canMakePayment ||
          isProcessing
        );
      }),
      distinctUntilChanged(),
    );

    this.selectedPaymentProvider$.pipe(
      distinctUntilChanged(),
      skip(1),
      tap(() => this.resetWireTransferForm()),
      untilDestroyed(this),
    ).subscribe();

    this.syncOrderToForm();
  }

  createWireTransferForm(): void {
    this.wireTransferForm = this.fb.group({
      orderId: new FormControl(null),
      file: new FormControl(null, [Validators.required]),
    });
  }

  resetWireTransferForm(): void {
    this.wireTransferForm.patchValue({
      orderId: this.order?.id ?? null,
      file: null,
    });
  }

  wireTransferProofChanged(event: DropzoneFileChangedEvent): void {
    if (event.type === DropzoneAction.Added) {
      this.wireTransferForm.controls.file.setValue(event.files[0]);
    } else if (event.type === DropzoneAction.Removed) {
      this.wireTransferForm.controls.file.setValue(null);
    }
  }

  onPaymentOptionChange(change: MatRadioChange) {
    this.checkoutFlowFacade.setSelectedPaymentProvider(change.value);
  }

  submitPayment(): void {
    combineLatest([
      this.isPaymentLocked$.pipe(take(1)),
      this.selectedPaymentProvider$.pipe(take(1)),
      this.orderSubject.pipe(take(1)),
    ]).pipe(
      switchMap(([isPaymentLocked, selectedPaymentProvider, order]) =>
        this.checkoutFlowFacade.submitSelectedPaymentMethod$({
          isPaymentLocked,
          selectedPaymentProvider,
          order,
          wireTransferFile: this.wireTransferForm.controls.file.value,
          redirectUrl: this.redirectUrl,
          redirectQueryParams: this.redirectQueryParams,
          shouldRedirect: this.shouldRedirect,
        })
      ),
      untilDestroyed(this),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.orderSubject.complete();
  }

  private syncOrderToForm(): void {
    if (!this.wireTransferForm) {
      return;
    }

    this.wireTransferForm.patchValue({
      orderId: this.order?.id ?? null,
    });
  }
}
